import {useCallback, useMemo} from 'react';
import {useNavigate} from 'react-router-dom';
import {format, parse, parseISO} from 'date-fns';

import {BillData, SaoLucasBillItem} from 'domain/usecases';
import {FeaturesEnum} from 'presentation/constants';
import {useFeatureFlag} from 'presentation/hooks/useFeatureFlag';
import {useAccountDataStore} from 'presentation/pages/private/store/useAccountDataStateStore';
import {mapSaoLucasBillToBillData} from '../../services/getSaoLucasBills/mapper';
import {useGetOpenObligationListQuery} from '../../queries/useGetOpenObligationListQuery';
import {useGetSaoLucasBillsQuery} from '../../queries/useGetSaoLucasBillsQuery';
import {MergedBillItem} from './types';

const toDateKey = (date: Date): string => format(date, 'yyyy-MM-dd');

const buildMergedBills = (
	paymentsBills: BillData[],
	saoLucasBillItems: SaoLucasBillItem[],
): MergedBillItem[] => {
	const saoLucasMap = new Map<string, SaoLucasBillItem>();
	for (const item of saoLucasBillItems) {
		saoLucasMap.set(toDateKey(parseISO(item.vencimento)), item);
		saoLucasMap.set(toDateKey(parseISO(item.vencimentoOriginal)), item);
	}

	const usedSaoLucasItems = new Set<SaoLucasBillItem>();
	const result: {item: MergedBillItem; date: Date}[] = [];

	for (const bill of paymentsBills) {
		const candidateKeys: string[] = [
			toDateKey(parse(bill.dtPrevista, 'dd/MM/yyyy', new Date())),
		];

		if (bill.dtCompInicio) {
			candidateKeys.push(toDateKey(parse(bill.dtCompInicio, 'dd/MM/yyyy', new Date())));
		}

		if (bill.linhaDigitavel?.dtVencimento) {
			candidateKeys.push(
				toDateKey(parse(bill.linhaDigitavel.dtVencimento, 'dd/MM/yyyy', new Date())),
			);
		}

		let matchedSaoLucasItem: SaoLucasBillItem | undefined;
		for (const key of candidateKeys) {
			if (saoLucasMap.has(key)) {
				matchedSaoLucasItem = saoLucasMap.get(key)!;
				break;
			}
		}

		const date = bill.dtCompInicio
			? parse(bill.dtCompInicio, 'dd/MM/yyyy', new Date())
			: parse(bill.dtPrevista, 'dd/MM/yyyy', new Date());

		if (matchedSaoLucasItem) {
			usedSaoLucasItems.add(matchedSaoLucasItem);
			result.push({
				item: {bill: {...bill, urlBoletoMigrado: matchedSaoLucasItem.downloadLink}},
				date,
			});
		} else {
			result.push({item: {bill}, date});
		}
	}

	for (const item of saoLucasBillItems) {
		if (!usedSaoLucasItems.has(item)) {
			result.push({
				item: {bill: mapSaoLucasBillToBillData(item), downloadLink: item.downloadLink},
				date: parseISO(item.vencimento),
			});
		}
	}

	result.sort((a, b) => a.date.getTime() - b.date.getTime());
	return result.map(({item}) => item);
};

export const useOpenBills = () => {
	const {isEnabled: saoLucasFlagIsEnabled} = useFeatureFlag(
		FeaturesEnum.PORTAL_FEATURE_BILLS_SAO_LUCAS,
	);
	const {beneficiary, accesses} = useAccountDataStore(store => store.accountData);
	const {credentialCodeSaoLucas, flSaoLucasSaude} = accesses;
	const {dtNascimentoUsuarioC} = beneficiary;
	const {data: openBills, isLoading, error: isError, refetch} = useGetOpenObligationListQuery();
	const saoLucasEnabled = saoLucasFlagIsEnabled && !!flSaoLucasSaude && !!credentialCodeSaoLucas;
	const {
		data: saoLucasBills,
		isLoading: isSaoLucasLoading,
		error: isSaoLucasError,
		refetch: refetchSaoLucas,
	} = useGetSaoLucasBillsQuery(saoLucasEnabled, {
		userCode: credentialCodeSaoLucas ?? '',
		birthDate: dtNascimentoUsuarioC,
	});
	const navigate = useNavigate();

	const handleNavigation = useCallback(
		(route: string) => {
			navigate(route);
		},
		[navigate],
	);

	const handleRefetch = useCallback(() => {
		refetch();
		refetchSaoLucas();
	}, [refetch, refetchSaoLucas]);

	const mergedBills = useMemo((): MergedBillItem[] => {
		if (!saoLucasEnabled) {
			return (openBills ?? []).map(bill => ({bill}));
		}
		return buildMergedBills(openBills ?? [], saoLucasBills ?? []);
	}, [openBills, saoLucasBills, saoLucasEnabled]);

	const isEmpty = mergedBills.length === 0;

	const showDelayAlert = mergedBills.some(
		({bill}) => (bill.linhaDigitavel?.qtDiasAtraso ?? 0) >= 5,
	);

	const combinedIsLoading = isLoading || (saoLucasEnabled && isSaoLucasLoading);
	const combinedIsError = isError || (saoLucasEnabled && isSaoLucasError);

	return {
		mergedBills,
		handleNavigation,
		isEmpty,
		showDelayAlert,
		isLoading: combinedIsLoading,
		isError: combinedIsError,
		refetch: handleRefetch,
	};
};
